import { useMemo, useCallback, useState } from 'react';
import {
  Flex,
  Text,
  Pill,
  Box,
  SelectButton,
  SelectOption,
  NumberInput,
  useBreakpointIndex,
} from '@infotrack/zenith-ui';
import type { TopFiveItemType } from '@/api/types/topFiveItemType';
import type { ITopFiveApiV2EndpointsTopFiveGetTopFiveByIdTopFivePermissionsDto } from '@/api/models';
import {
  containerStyles,
  containerErrorStyles,
  iconTextSectionStyles,
  progressSectionStyles,
  numberedIconStyles,
  progressTextStyles,
  progressInputStyles,
  iconTextInnerStyles,
  itemNameWrapperStyles,
  selectButtonWrapperStyles,
  progressBarContainerStyles,
  progressTextMobileStyles,
  progressInputDesktopStyles,
  progressTextNonEditableStyles,
} from './topFiveItem.styles';
import { Progress } from '../../../shared/components/progress/progress';
import { Colors } from '@infotrack/zenith-ui/core/components/theme/colors';
import {
  STATUS_OPTIONS,
  RESULT_OPTIONS,
  STATUS_VALUES,
  RESULT_VALUES,
  getStatusInfo,
} from '../constants/topFiveConstants';
import { TopFiveItemActions } from './topFiveItemActions';

type PendingItemChange = {
  topFiveItemId: number;
  completion?: number;
  status?: string;
  result?: string;
};

type TopFiveItemProps = {
  item: TopFiveItemType;
  itemNumber: number;
  onItemChange?: (changes: Partial<PendingItemChange>) => void;
  permissions?: ITopFiveApiV2EndpointsTopFiveGetTopFiveByIdTopFivePermissionsDto;
  /**
   * @default false
   */
  showActions?: boolean;
  onActionClick?: (
    action: 'comment' | 'history' | 'group',
    item: TopFiveItemType
  ) => void;
  /**
   * @default false
   */
  isInteractive?: boolean;
  /**
   * @default false
   */
  isExpanded?: boolean;
  onToggleExpansion?: (itemId: number) => void;
};

export const TopFiveItem = ({
  item,
  itemNumber,
  onItemChange,
  permissions,
  showActions = false,
  onActionClick,
  isInteractive = false,
  isExpanded = false,
}: TopFiveItemProps) => {
  const [localProgress, setLocalProgress] = useState(item.completion || 0);
  const [editingProgress, setEditingProgress] = useState<string>('');
  const breakpointIndex = useBreakpointIndex();
  const isDesktop = breakpointIndex >= 2; // desktopSmall and above

  // Memoized computations
  const statusInfo = useMemo(() => {
    const result = permissions?.isAchievedEditEnabled ? null : item.result;
    return getStatusInfo(item.status, result);
  }, [item.status, item.result, permissions?.isAchievedEditEnabled]);

  const canEditProgress = useMemo(
    () => isInteractive && (permissions?.isUsersOwnTopFive || permissions?.isAllowedToEditCompanysTopFive),
    [isInteractive, permissions?.isUsersOwnTopFive, permissions?.isAllowedToEditCompanysTopFive]
  );

  const progressValue = useMemo(
    () =>
      Math.max(
        0,
        Math.min(100, canEditProgress ? localProgress : item.completion || 0)
      ),
    [canEditProgress, localProgress, item.completion]
  );

  const handleProgressChange = useCallback(
    (newValue: number) => {
      setLocalProgress(newValue);
      onItemChange?.({ completion: newValue });
    },
    [onItemChange]
  );

  const handleProgressBlur = useCallback(() => {
    const numValue = Number(editingProgress);
    if (editingProgress === '' || isNaN(numValue)) {
      handleProgressChange(0);
    } else {
      const clampedValue = Math.max(0, Math.min(100, numValue));
      handleProgressChange(clampedValue);
    }
    setEditingProgress('');
  }, [editingProgress, handleProgressChange]);

  const handleStatusChange = useCallback(
    (params: { selectedOption?: SelectOption<string> }) => {
      if (params.selectedOption) {
        onItemChange?.({ status: String(params.selectedOption.value) });
      }
    },
    [onItemChange]
  );

  const handleResultChange = useCallback(
    (params: { selectedOption?: SelectOption<string> }) => {
      if (params.selectedOption) {
        onItemChange?.({ result: String(params.selectedOption.value) });
      }
    },
    [onItemChange]
  );

  const isStrikethrough = useMemo(
    () => item.result === RESULT_VALUES.NA,
    [item.result]
  );

  const hasErrorBackground = useMemo(
    () =>
      item.status === STATUS_VALUES.RED || item.result === RESULT_VALUES.FAIL,
    [item.status, item.result]
  );

  const hasTeamMembers = useMemo(
    () =>
      Boolean(
        item.staffWithSameTopFiveItem &&
          item.staffWithSameTopFiveItem.length > 0
      ),
    [item]
  );

  // Reusable component definitions
  const numberedIcon = <Box sx={numberedIconStyles}>{itemNumber}</Box>;

  const itemName = (
    <Box sx={itemNameWrapperStyles}>
      <Text variant="subtitle" color="primary/600">
        <span
          style={{
            textDecoration: isStrikethrough ? 'line-through' : 'none',
          }}
        >
          {item.name}
        </span>
      </Text>
    </Box>
  );

  const weightPill = (
    <Pill variant="static" iconProps={{ name: 'weight', variant: 'outlined' }}>
      {`${item.weight ?? 0}%`}
    </Pill>
  );

  const resultSelect = permissions?.isAchievedEditEnabled ? (
    <Flex sx={selectButtonWrapperStyles}>
      <SelectButton
        key={`result-${item.topFiveItemId}-${item.result}`}
        options={RESULT_OPTIONS}
        value={item.result ?? ''}
        onSelect={handleResultChange}
        size="small"
        fullWidth={true}
        placeholder="Result"
        aria-label="Select result"
      />
    </Flex>
  ) : null;

  const progressSection = (
    <Flex sx={progressSectionStyles}>
      <Box sx={progressBarContainerStyles}>
        <Progress
          color={statusInfo.color}
          progressValue={progressValue}
          width="100%"
          isInteractive={canEditProgress}
          onChange={handleProgressChange}
        />
      </Box>
      <Box sx={canEditProgress ? progressInputStyles : progressTextStyles}>
        {canEditProgress ? (
          <>
            <Box sx={progressTextMobileStyles}>
              <Text variant="subtitle" color="primary/600">
                {progressValue}%
              </Text>
            </Box>
            <Box sx={progressInputDesktopStyles}>
              <NumberInput
                percentage
                value={
                  editingProgress !== ''
                    ? Number(editingProgress)
                    : progressValue
                }
                onChange={e => {
                  setEditingProgress(e.target.value);
                }}
                onBlur={handleProgressBlur}
                size="small"
                textPosition="right"
                aria-label="Edit progress percentage"
              />
            </Box>
          </>
        ) : (
          <Box sx={progressTextNonEditableStyles}>
            <Text variant="subtitle" color="primary/600">
              {progressValue}%
            </Text>
          </Box>
        )}
      </Box>
    </Flex>
  );

  const statusControl = canEditProgress ? (
    <Flex sx={selectButtonWrapperStyles}>
      <SelectButton
        key={`status-${item.topFiveItemId}-${item.status}`}
        options={STATUS_OPTIONS}
        value={item.status || undefined}
        onSelect={handleStatusChange}
        size="small"
        fullWidth={true}
        placeholder="Status"
        aria-label="Select status"
      />
    </Flex>
  ) : (
    <Pill variant="static" background={statusInfo.pillBgColor as Colors}>
      {statusInfo.text}
    </Pill>
  );

  const actions = showActions ? (
    <TopFiveItemActions
      item={item}
      hasTeamMembers={hasTeamMembers}
      isExpanded={isExpanded || false}
      onActionClick={onActionClick}
    />
  ) : null;

  // Desktop layout
  if (isDesktop) {
    return (
      <Flex sx={hasErrorBackground ? containerErrorStyles : containerStyles}>
        <Flex sx={iconTextSectionStyles}>
          <Flex sx={iconTextInnerStyles}>
            {numberedIcon}
            {itemName}
          </Flex>
        </Flex>
        {resultSelect}
        {weightPill}
        {progressSection}
        {statusControl}
        {actions}
      </Flex>
    );
  }

  // Mobile layout
  return (
    <Flex sx={hasErrorBackground ? containerErrorStyles : containerStyles}>
      <Flex sx={iconTextSectionStyles}>
        <Flex sx={iconTextInnerStyles}>
          {numberedIcon}
          {itemName}
        </Flex>
        {weightPill}
        {actions}
      </Flex>
      {resultSelect}
      {progressSection}
      {statusControl}
    </Flex>
  );
};
